import { If } from "components";
import { useBoolean } from "hooks";
import { useTranslation } from "react-i18next";
import { useEffect, useRef } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { toast } from "react-hot-toast";
import { Button, Modal } from "ui";

import { useAiProductInfo } from "../../apis/useAiProductInfo";
import { ProductFormValues } from "../forms/ProductDetailsForm/ProductDetailsForm.types";

import styles from "./AiCopyAssistantBlock.module.scss";

const STAGE_ANALYZING_THRESHOLD_MS = 5_000;

/**
 * Top-of-form AI assistant that hands the variant images to the
 * /admin/ai/product-info endpoint, polls until it's done, and pre-fills
 * the product description plus every variant name with the generated
 * copy.
 *
 * The button stays disabled until at least one variant image has been
 * uploaded (the AI service fetches the URLs directly, so unpublished
 * media won't reach it). When the form already has copy filled in we
 * pop a confirmation modal first — generation overwrites every field,
 * so a fat-finger click on the wrong day would silently nuke a finished
 * description.
 */
export const AiCopyAssistantBlock = () => {
  const { t, i18n } = useTranslation();
  const { control, setValue, getValues } = useFormContext<ProductFormValues>();

  // useWatch is the reliable way to react to nested useFieldArray edits:
  // plain watch("variants") sometimes hangs onto a stale reference after
  // `remove(...)` is called on a child array, so the button wouldn't
  // re-disable when the last image was deleted.
  const variants = useWatch({ control, name: "variants" });
  const description = useWatch({ control, name: "description" });

  const seen = new Set<string>();
  const mediaUrls: string[] = [];
  for (const variant of variants ?? []) {
    for (const image of variant?.images ?? []) {
      if (image?.url && !seen.has(image.url)) {
        seen.add(image.url);
        mediaUrls.push(image.url);
      }
    }
  }

  const hasImages = mediaUrls.length > 0;
  const hasExistingCopy =
    !!description?.trim() ||
    (variants ?? []).some((v) => v?.name?.trim().length);

  const {
    value: isConfirmOpen,
    setTrue: openConfirm,
    setFalse: closeConfirm
  } = useBoolean(false);

  const ai = useAiProductInfo();

  const lastAppliedWorkflowRef = useRef<string | null>(null);

  const startGeneration = () => {
    // Map i18next's `en-US` / `fr-FR` style locale strings down to the
    // bare language codes the backend's ELanguage enum accepts. Anything
    // else (e.g. an admin viewing the UI in Russian) falls back to English
    // server-side because we don't ship a French/English-only fallback.
    const base = (i18n.language ?? "").toLowerCase().split(/[-_]/)[0];
    const language =
      base === "fr" || base === "en"
        ? (base as "fr" | "en")
        : undefined;
    ai.triggerGeneration({ mediaUrls, version: "1", language });
  };

  const onGenerateClick = () => {
    if (!hasImages) return;
    if (hasExistingCopy) {
      openConfirm();
      return;
    }
    startGeneration();
  };

  const onConfirmOverwrite = () => {
    closeConfirm();
    startGeneration();
  };

  // Apply the completed result exactly once per workflow — re-runs of
  // this effect (e.g. because variants change reference) must not loop.
  useEffect(() => {
    if (!ai.isCompleted || !ai.result) return;
    const formValues = getValues();
    const stamp = JSON.stringify(ai.result);
    if (lastAppliedWorkflowRef.current === stamp) return;
    lastAppliedWorkflowRef.current = stamp;

    setValue("description", ai.result.description, {
      shouldDirty: true,
      shouldTouch: true
    });
    (formValues.variants ?? []).forEach((_, index) => {
      setValue(`variants.${index}.name`, ai.result!.title, {
        shouldDirty: true,
        shouldTouch: true
      });
    });
    toast.success(t("AI copy applied. Review and tweak before saving."));
  }, [ai.isCompleted, ai.result, setValue, getValues, t]);

  useEffect(() => {
    if (ai.triggerError) {
      toast.error(
        t("Couldn't start AI generation. Try again in a few seconds.")
      );
    }
  }, [ai.triggerError, t]);

  useEffect(() => {
    if (ai.isFailed) {
      toast.error(
        t("AI generation failed. Try again or fill the fields manually.")
      );
    }
  }, [ai.isFailed, t]);

  const isBusy = ai.isTriggering || ai.isPolling;
  const buttonLabel = isBusy
    ? t("Generating…")
    : t("Generate copy from images");

  const stageMessage = (() => {
    if (!ai.isPolling) return null;
    if (ai.elapsedMs < STAGE_ANALYZING_THRESHOLD_MS) {
      return t("Analyzing images…");
    }
    return t("Generating copy…");
  })();

  return (
    <div className={styles.aiBlock}>
      <div className={styles.aiBlock_header}>
        <div>
          <div className={styles.aiBlock_title}>
            {t("AI copy assistant")}
          </div>
          <div className={styles.aiBlock_hint}>
            {hasImages
              ? t(
                  "Generates a product title and description from the variant images. Review the output before saving."
                )
              : t(
                  "Upload at least one variant image to enable AI generation."
                )}
          </div>
        </div>
        <Button
          type="button"
          onClick={onGenerateClick}
          disabled={!hasImages || isBusy}
        >
          {buttonLabel}
        </Button>
      </div>

      <If condition={!!stageMessage}>
        <div className={styles.aiBlock_status}>{stageMessage}</div>
      </If>

      <If condition={isConfirmOpen}>
        <Modal
          isOpen={isConfirmOpen}
          title={t("Replace existing copy?")}
          okText={t("Replace")}
          onOk={onConfirmOverwrite}
          onClose={closeConfirm}
        >
          <p>
            {t(
              "Generating will overwrite the current product description and every variant name. Continue?"
            )}
          </p>
        </Modal>
      </If>
    </div>
  );
};
